import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Skeleton,
  Modal,
  Input,
  TextArea,
  RadioGroup,
  FileUpload,
  ErrorBoundary,
  useToast,
  Timeline,
} from '@bbpos/ui';
import type { TimelineItem, ToastVariant } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import {
  useQuotation,
  useQuotationChangeRequests,
  useQuotationSends,
  useCreateSalesOrderFromQuote,
  useSendQuotation,
  useCancelQuotation,
  QuotationStatusBadge,
  StockAvailabilityBadge,
} from '@/features/quotations';
import type {
  SendChannel,
  QuotationItem,
  QuotationRefundMode,
} from '@/features/quotations';
import { uploadPurchaseDocument, DocumentUploadError, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES } from '@/features/vendor-bills';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { managerApprovalService } from '@/services/manager-approval.service';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';
import { ConvertIcon, SendIcon, CopyIcon } from './icons';
import { useTenantDial } from '@/hooks/use-tenant-dial';

// ─── Status accent config (left-rail tone tile only — no borders) ───────────

const STATUS_ACCENT: Record<string, { tint: string; rail: string }> = {
  ACTIVE: {
    tint: 'bg-brand-50/80 dark:bg-brand-900/20',
    rail: 'before:bg-brand-500',
  },
  CONVERTED: {
    tint: 'bg-success-50/80 dark:bg-success-900/20',
    rail: 'before:bg-success-500',
  },
  EXPIRED: {
    tint: 'bg-danger-50/80 dark:bg-danger-900/20',
    rail: 'before:bg-danger-500',
  },
  APPROVED: {
    tint: 'bg-violet-50/80 dark:bg-violet-900/20',
    rail: 'before:bg-violet-500',
  },
  CANCELLED: {
    tint: 'bg-neutral-50/80 dark:bg-neutral-800/40',
    rail: 'before:bg-neutral-400',
  },
  REJECTED: {
    tint: 'bg-danger-50/80 dark:bg-danger-900/20',
    rail: 'before:bg-danger-500',
  },
};

const STATUS_RAIL_BASE =
  "relative pl-5 before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full";

// ─── Validity helpers ───────────────────────────────────────────────────────

function validityColor(validUntil: string): string {
  const now = Date.now();
  const expiry = new Date(validUntil).getTime();
  const twoDays = 2 * 24 * 60 * 60 * 1000;
  if (expiry < now) return 'text-danger-100';
  if (expiry - now < twoDays) return 'text-warning-100';
  return 'text-brand-100';
}

function validityLabel(validUntil: string): string {
  const now = Date.now();
  const expiry = new Date(validUntil).getTime();
  if (expiry < now) return 'Expired';
  const daysLeft = Math.ceil((expiry - now) / (24 * 60 * 60 * 1000));
  if (daysLeft <= 1) return 'Expires today';
  return `${daysLeft} days remaining`;
}

// ─── Friendly error mapping ─────────────────────────────────────────────────

// Refusals whose API message is already written for the person at the screen.
const FRIENDLY_REFUSAL_CODES = new Set([
  'APPROVAL_REQUIRED',
  'APPROVAL_INVALID',
  'INVALID_PIN',
  'PIN_NOT_SET',
  'PIN_LOCKED',
  'APPROVER_NOT_ALLOWED',
]);

function mapMutationError(err: unknown): { title: string; variant: ToastVariant } {
  if (err instanceof ApiError) {
    // QA SS4-005: never show the role guard's internal text.
    if (err.statusCode === 403 || err.statusCode === 429) {
      if (err.code && FRIENDLY_REFUSAL_CODES.has(err.code)) {
        return { title: err.message, variant: 'error' };
      }
      return { title: "You don't have permission to do this. Ask a manager.", variant: 'error' };
    }
    const msg = err.message.toLowerCase();
    if (err.statusCode === 409 && (msg.includes('expired') || msg.includes('validity'))) {
      return {
        title: 'This quotation has expired and cannot be converted',
        variant: 'warning',
      };
    }
    if (
      err.statusCode === 409 &&
      (msg.includes('already converted') || msg.includes('already been converted') || msg.includes('no longer active'))
    ) {
      return {
        title: 'This quotation has already been converted',
        variant: 'error',
      };
    }
    return { title: err.message, variant: 'error' };
  }
  return { title: (err as Error).message ?? 'Unexpected error', variant: 'error' };
}

// ─── Detail Content ─────────────────────────────────────────────────────────

function QuotationDetailContent() {
  const currency = useTenantCurrency();
  const dial = useTenantDial();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const tax = useTaxRegime();

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Refund choice for a down payment held on the quote (the API requires one).
  const [refundMode, setRefundMode] = useState<QuotationRefundMode>('ORIGINAL');
  // Below MANAGER, a manager approves the cancel with their POS PIN (SS4-005).
  const [approverId, setApproverId] = useState('');
  const [approverPin, setApproverPin] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  // "Convert to Sales Order" form — the customer's purchase order details.
  const [poNumber, setPoNumber] = useState('');
  const [poNumberError, setPoNumberError] = useState<string | null>(null);
  const [poDate, setPoDate] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [soNotes, setSoNotes] = useState('');
  const [poFile, setPoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sendChannel, setSendChannel] = useState<SendChannel>('LINK');
  const [sendRecipient, setSendRecipient] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');

  const { data: quotation, isLoading, error } = useQuotation(id);
  const { data: changeRequests = [] } = useQuotationChangeRequests(id);
  const { data: sends = [] } = useQuotationSends(id);

  const createSoMutation = useCreateSalesOrderFromQuote();
  const sendMutation = useSendQuotation();
  const cancelMutation = useCancelQuotation();

  // ── Realtime: listen for public-page events so the detail page reflects
  // customer actions (approve / request-changes) without a manual refresh.
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? null);
  const role = useAuthStore((s) => s.profile?.role);
  const isManager = (ROLE_LEVELS[role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER;
  const { data: approvers = [], isLoading: approversLoading } = useQuery({
    queryKey: ['manager-approval', tenantId, 'approvers', quotation?.location_id],
    queryFn: () => managerApprovalService.listApprovers(quotation?.location_id),
    enabled: showCancelModal && !isManager && !!quotation,
  });
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user || !id) return;
    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });
    socketRef.current = socket;

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['quotations', tenantId, 'detail', id] });
      queryClient.invalidateQueries({
        queryKey: ['quotations', tenantId, 'change-requests', id],
      });
    };
    const onApproved = (payload: { quotation_id: string }) => {
      if (payload.quotation_id === id) {
        invalidate();
        toast({ title: 'Customer approved this quote', variant: 'success' });
      }
    };
    const onChanges = (payload: { quotation_id: string }) => {
      if (payload.quotation_id === id) {
        invalidate();
        toast({ title: 'Customer requested changes', variant: 'info' });
      }
    };
    const onRejected = (payload: { quotation_id: string }) => {
      if (payload.quotation_id === id) {
        invalidate();
        toast({ title: 'Customer declined this quote', variant: 'error' });
      }
    };
    socket.on('quote:approved', onApproved);
    socket.on('quote:changes_requested', onChanges);
    socket.on('quote:rejected', onRejected);

    return () => {
      socket.off('quote:approved', onApproved);
      socket.off('quote:changes_requested', onChanges);
      socket.off('quote:rejected', onRejected);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, tenantId, id, queryClient, toast]);

  // Auto-open modals from URL action param
  useEffect(() => {
    if (!quotation) return;
    const action = searchParams.get('action');
    // APPROVED is included so the list's `c` shortcut (which may pick an
    // approved quote) lands on an open modal rather than a silent no-op.
    if (action === 'convert' && (quotation.status === 'ACTIVE' || quotation.status === 'APPROVED')) {
      setShowConvertModal(true);
    }
    if (action === 'send' && (quotation.status === 'ACTIVE' || quotation.status === 'APPROVED')) {
      setShowSendModal(true);
    }
  }, [quotation, searchParams]);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/quotations');
    return null;
  }

  if (isLoading || !quotation) {
    return (
      <>
        <Skeleton variant="rect" height={104} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton variant="rect" height={200} className="rounded-2xl" />
            <Skeleton variant="rect" height={300} className="rounded-2xl" />
            <Skeleton variant="rect" height={200} className="rounded-2xl" />
          </div>
          <div className="space-y-4">
            <Skeleton variant="rect" height={160} className="rounded-2xl" />
            <Skeleton variant="rect" height={200} className="rounded-2xl" />
          </div>
        </div>
      </>
    );
  }

  const statusAccent = STATUS_ACCENT[quotation.status] ?? STATUS_ACCENT.ACTIVE;
  const items = quotation.items ?? [];
  const isActive = quotation.status === 'ACTIVE';
  const isApproved = quotation.status === 'APPROVED';
  const advancePaid = quotation.advance_paid ?? 0;
  const canAct = isActive || isApproved;
  const hasOutOfStock = items.some((i) => i.stock_availability === 'OUT_OF_STOCK');

  function openConvertModal() {
    setPoNumber('');
    setPoNumberError(null);
    setPoDate('');
    setExpectedDelivery('');
    setShippingAddress('');
    setSoNotes('');
    setPoFile(null);
    setShowConvertModal(true);
  }

  // Record the customer's PO and turn the quote into a Sales Order. The PO
  // document (if any) is uploaded first; a failed upload aborts the conversion
  // so we never create an order that claims a document it doesn't have.
  async function handleCreateSalesOrder() {
    if (!id || !quotation) return;
    const po = poNumber.trim();
    if (!po) {
      setPoNumberError('Customer PO number is required');
      return;
    }
    let document: { file_key: string; file_name?: string; content_type?: string; size_bytes?: number } | undefined;
    if (poFile) {
      setUploading(true);
      try {
        document = await uploadPurchaseDocument(poFile);
      } catch (err) {
        setUploading(false);
        toast({ title: err instanceof DocumentUploadError ? err.message : 'PO document upload failed', variant: 'error' });
        return;
      }
      setUploading(false);
    }
    createSoMutation.mutate(
      {
        quotation_id: id,
        customer_po_number: po,
        customer_po_date: poDate || undefined,
        expected_delivery_date: expectedDelivery || undefined,
        shipping_address: shippingAddress.trim() || undefined,
        notes: soNotes.trim() || undefined,
        document: document ? { ...document, kind: 'CUSTOMER_PO' } : undefined,
      },
      {
        onSuccess: (so) => {
          toast({ title: `Sales Order ${so.so_number} created from ${quotation.quote_number}`, variant: 'success' });
          setShowConvertModal(false);
          navigate(`/sales-orders/${so.id}`);
        },
        onError: (err) => toast(mapMutationError(err)),
      },
    );
  }

  function handleSend() {
    if (!id) return;
    sendMutation.mutate(
      { id, input: { channel: sendChannel, recipient: sendRecipient || undefined } },
      {
        onSuccess: (result) => {
          if (sendChannel === 'LINK' && result?.approval_link) {
            setGeneratedLink(result.approval_link);
            toast({ title: 'Approval link generated', variant: 'success' });
          } else {
            toast({ title: `Quote sent via ${sendChannel}`, variant: 'success' });
            setShowSendModal(false);
          }
        },
        onError: (err) => toast(mapMutationError(err)),
      },
    );
  }

  function copyLink() {
    navigator.clipboard.writeText(generatedLink);
    toast({ title: 'Link copied to clipboard', variant: 'success' });
  }

  function closeCancelModal() {
    setShowCancelModal(false);
    setApproverPin('');
  }

  async function handleCancel() {
    if (!id) return;
    let approvalToken: string | undefined;
    if (!isManager) {
      setVerifyingPin(true);
      try {
        const grant = await managerApprovalService.verify({
          approver_id: approverId,
          pin: approverPin,
          action: 'QUOTE_CANCEL',
          subject_id: id,
          location_id: quotation?.location_id,
        });
        approvalToken = grant.approval_token;
      } catch (err) {
        setApproverPin('');
        toast(mapMutationError(err));
        return;
      } finally {
        setVerifyingPin(false);
      }
    }
    cancelMutation.mutate(
      {
        id,
        refundMode: advancePaid > 0 ? refundMode : undefined,
        ...(approvalToken ? { approvalToken } : {}),
      },
      {
        onSuccess: () => {
          closeCancelModal();
          toast({ title: 'Quotation cancelled', variant: 'success' });
        },
        onError: (err) => toast(mapMutationError(err)),
      },
    );
  }

  // Build timeline
  const timelineItems: TimelineItem[] = [
    {
      id: 'created',
      type: 'pending',
      action: 'Quote created',
      timestamp: quotation.created_at,
    },
  ];
  if (quotation.approved_at) {
    timelineItems.push({
      id: 'approved',
      type: 'approved',
      action: quotation.approval ? `Approved by ${quotation.approval.name}` : 'Customer approved',
      timestamp: quotation.approved_at,
    });
  }
  if (quotation.status === 'CONVERTED') {
    timelineItems.push({
      id: 'converted',
      type: 'success',
      action: quotation.sales_order
        ? `Converted to Sales Order ${quotation.sales_order.so_number}`
        : 'Converted to sale',
      timestamp: quotation.updated_at,
    });
  }
  if (quotation.status === 'EXPIRED') {
    timelineItems.push({
      id: 'expired',
      type: 'rejected',
      action: 'Quote expired',
      timestamp: quotation.valid_until,
    });
  }
  if (quotation.status === 'REJECTED' && quotation.rejected_at) {
    timelineItems.push({
      id: 'rejected',
      type: 'rejected',
      action: 'Customer declined',
      description: quotation.rejection_reason ?? undefined,
      timestamp: quotation.rejected_at,
    });
  }
  if (quotation.status === 'CANCELLED') {
    timelineItems.push({
      id: 'cancelled',
      type: 'rejected',
      action: 'Quote cancelled',
      timestamp: quotation.updated_at,
    });
  }
  for (const cr of changeRequests) {
    timelineItems.push({
      id: `cr-${cr.id}`,
      type: 'pending',
      action: 'Customer requested changes',
      description: cr.notes,
      timestamp: cr.created_at,
    });
  }
  for (const s of sends) {
    timelineItems.push({
      id: `send-${s.id}`,
      type: s.status === 'FAILED' ? 'failed' : 'info',
      action:
        s.channel === 'LINK'
          ? 'Approval link shared'
          : `Sent via ${s.channel === 'WHATSAPP' ? 'WhatsApp' : s.channel === 'EMAIL' ? 'Email' : s.channel}`,
      description: s.channel === 'LINK' ? undefined : s.recipient,
      timestamp: s.created_at,
    });
  }
  // Render newest activity on top — Timeline preserves the array order.
  timelineItems.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  function handleCopyPublicLink() {
    if (!id) return;
    sendMutation.mutate(
      { id, input: { channel: 'LINK' } },
      {
        onSuccess: (result) => {
          const link = result?.approval_link;
          if (!link) return;
          navigator.clipboard
            .writeText(link)
            .then(() => toast({ title: 'Public link copied', variant: 'success' }))
            .catch(() => {
              // Fallback: surface the link in the Send Quote modal so staff can copy manually.
              setGeneratedLink(link);
              setShowSendModal(true);
            });
        },
        onError: (err) => toast(mapMutationError(err)),
      },
    );
  }

  return (
    <>
      {/* Gradient header with quote number, status, and total */}
      <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 to-indigo-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(37,99,235,0.45)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-mono text-2xl font-bold tracking-tight tabular-nums text-white">
                {quotation.quote_number}
              </h1>
              <QuotationStatusBadge status={quotation.status} size="md" />
            </div>
            <p className={`mt-1 text-sm ${validityColor(quotation.valid_until)}`}>
              Valid until{' '}
              <span className="font-mono tabular-nums">
                {new Date(quotation.valid_until).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>{' '}
              · {validityLabel(quotation.valid_until)}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-3xl font-bold tabular-nums leading-none">
              {formatCurrency(quotation.grand_total, currency)}
            </p>
            <p className="text-sm text-brand-200 mt-1">Grand Total</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
        {/* Left: Details + Items + Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote Information */}
          <div className={cn(GLASS, 'p-6')}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-4">
              Quote Information
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Quote Number
                </dt>
                <dd className="font-mono text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">
                  {quotation.quote_number}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Date Created
                </dt>
                <dd className="font-mono text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">
                  {new Date(quotation.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Customer
                </dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">
                  {quotation.customer_name || (
                    <span className="italic text-neutral-400">Walk-in</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Phone
                </dt>
                <dd className="font-mono text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">
                  {quotation.customer_phone || '—'}
                </dd>
              </div>
              {quotation.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                    Notes
                  </dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">
                    {quotation.notes}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Items list — no borders, alternating glass tint, mono numbers */}
          <div className={cn(GLASS, 'p-6')}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Items ({items.length})
              </h3>
              {hasOutOfStock && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-50 dark:bg-danger-900/30 px-2.5 py-0.5 text-xs font-semibold text-danger-700 dark:text-danger-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-danger-500 animate-pulse" />
                  Insufficient stock on conversion
                </span>
              )}
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Product
                    </th>
                    <th className="text-left px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      SKU
                    </th>
                    <th className="text-right px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Qty
                    </th>
                    <th className="text-right px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Unit Price
                    </th>
                    <th className="text-right px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      {tax.taxName}%
                    </th>
                    <th className="text-right px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Discount
                    </th>
                    <th className="text-right px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Total
                    </th>
                    <th className="text-left px-2 py-2 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Stock
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: QuotationItem, idx) => {
                    const oos = item.stock_availability === 'OUT_OF_STOCK';
                    const rowTint = oos
                      ? 'bg-danger-50/60 dark:bg-danger-900/20'
                      : idx % 2 === 0
                        ? 'bg-neutral-50/40 dark:bg-neutral-800/20'
                        : '';
                    return (
                      <tr key={item.id} className={rowTint}>
                        <td className="px-2 py-2.5 text-neutral-800 dark:text-neutral-200 font-medium">
                          {item.product_name}
                        </td>
                        <td className="px-2 py-2.5 font-mono text-neutral-500 dark:text-neutral-400 tabular-nums">
                          {item.product_sku}
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-neutral-800 dark:text-neutral-200 tabular-nums">
                          {item.quantity}
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-neutral-800 dark:text-neutral-200 tabular-nums">
                          {formatCurrency(item.unit_price, currency)}
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-neutral-600 dark:text-neutral-400 tabular-nums">
                          {item.gst_rate}%
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-neutral-600 dark:text-neutral-400 tabular-nums">
                          {item.line_discount > 0 ? formatCurrency(item.line_discount, currency) : '—'}
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-neutral-900 dark:text-neutral-100 font-semibold tabular-nums">
                          {formatCurrency(item.line_total, currency)}
                        </td>
                        <td className="px-2 py-2.5">
                          {item.stock_availability ? (
                            <StockAvailabilityBadge availability={item.stock_availability} />
                          ) : (
                            <span className="text-neutral-300 dark:text-neutral-600">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-5 space-y-2 pt-4 bg-gradient-to-r from-transparent via-neutral-200/40 to-transparent dark:via-neutral-700/40 [background-size:100%_1px] [background-position:top] [background-repeat:no-repeat]">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Subtotal</span>
                <span className="font-mono tabular-nums text-neutral-800 dark:text-neutral-200">
                  {formatCurrency(quotation.subtotal, currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Tax</span>
                <span className="font-mono tabular-nums text-neutral-800 dark:text-neutral-200">
                  {formatCurrency(quotation.tax_total, currency)}
                </span>
              </div>
              {quotation.discount_total > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500 dark:text-neutral-400">Discount</span>
                  <span className="font-mono tabular-nums text-danger-600 dark:text-danger-400">
                    −{formatCurrency(quotation.discount_total, currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold pt-2 mt-2 bg-gradient-to-r from-transparent via-neutral-200/60 to-transparent dark:via-neutral-700/60 [background-size:100%_1px] [background-position:top] [background-repeat:no-repeat]">
                <span className="text-neutral-900 dark:text-white">Grand Total</span>
                <span className="font-mono tabular-nums text-neutral-900 dark:text-white">
                  {formatCurrency(quotation.grand_total, currency)}
                </span>
              </div>
              {advancePaid > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500 dark:text-neutral-400">Down payment</span>
                    <span className="font-mono tabular-nums text-success-600 dark:text-success-400">
                      −{formatCurrency(advancePaid, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-neutral-700 dark:text-neutral-200">Balance due</span>
                    <span className="font-mono tabular-nums text-neutral-900 dark:text-white">
                      {formatCurrency(quotation.balance_due ?? quotation.grand_total - advancePaid, currency)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className={cn(GLASS, 'p-6')}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-4">
              Activity
            </h3>
            <Timeline items={timelineItems} />
          </div>
        </div>

        {/* Right: Status + Actions */}
        <div className="space-y-4">
          {/* Status Card — colored rail (no border) */}
          <div className={cn(
            'rounded-2xl shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)] backdrop-blur-sm p-5',
            STATUS_RAIL_BASE,
            statusAccent.tint,
            statusAccent.rail,
          )}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3">
              Status
            </h3>
            <QuotationStatusBadge status={quotation.status} size="md" />
            <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-3 leading-relaxed">
              {quotation.status === 'ACTIVE' && 'This quotation is open. Once the customer sends their purchase order, convert it to a Sales Order.'}
              {quotation.status === 'CONVERTED' && 'This quotation has been converted.'}
              {quotation.status === 'EXPIRED' && 'This quotation has expired and can no longer be converted.'}
              {quotation.status === 'APPROVED' && 'Customer has approved this quote. Ready to convert to a Sales Order.'}
              {quotation.status === 'CANCELLED' && 'This quotation has been cancelled.'}
              {quotation.status === 'REJECTED' && 'The customer declined this quote.'}
            </p>
            {quotation.status === 'REJECTED' && quotation.rejection_reason && (
              <p className="mt-2 text-sm text-danger-700 dark:text-danger-300 whitespace-pre-wrap">
                <span className="font-medium">Reason:</span> {quotation.rejection_reason}
              </p>
            )}
            {quotation.status === 'CONVERTED' && quotation.sales_order && (
              <Link
                to={`/sales-orders/${quotation.sales_order.id}`}
                className="mt-2 inline-block font-mono text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline tabular-nums"
              >
                Sales Order {quotation.sales_order.so_number} →
              </Link>
            )}
          </div>

          {/* Actions Card — state-specific actions only.
              ACTIVE   → Convert to Sales Order is the primary path; Send + Copy
                         link help staff get an approval from the customer.
              APPROVED → Convert remains the primary path. Send is intentionally
                         omitted — generating a fresh token would invalidate the
                         link the customer already used. "Copy approval link" is
                         retained so staff can re-share the (now read-only)
                         approval confirmation. */}
          {canAct && (
            <div className={cn(GLASS, 'p-5')}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3">
                Actions
              </h3>
              <div className="space-y-2">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={openConvertModal}
                >
                  <ConvertIcon className="h-4 w-4 mr-1.5" />
                  Convert to Sales Order
                </Button>
                {isActive && (
                  <>
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => {
                        setGeneratedLink('');
                        setSendRecipient('');
                        setShowSendModal(true);
                      }}
                    >
                      <SendIcon className="h-4 w-4 mr-1.5" />
                      Send Quote
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={handleCopyPublicLink}
                      disabled={sendMutation.isPending}
                      aria-label="Generate and copy the public approval link"
                    >
                      <CopyIcon className="h-4 w-4 mr-1.5" />
                      Copy public link
                    </Button>
                  </>
                )}
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={() => setShowCancelModal(true)}
                  disabled={cancelMutation.isPending}
                  aria-label="Cancel this quotation"
                >
                  Cancel quote
                </Button>
              </div>
            </div>
          )}

          {/* Customer Feedback — surfaces the most recent change request */}
          {changeRequests.length > 0 && (
            <div className={cn(
              'rounded-2xl shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)] backdrop-blur-sm p-5',
              STATUS_RAIL_BASE,
              'bg-violet-50/80 dark:bg-violet-900/20 before:bg-violet-500',
            )}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Customer Feedback
                </h3>
                <span className="font-mono text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                  {changeRequests.length} request{changeRequests.length === 1 ? '' : 's'}
                </span>
              </div>
              <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap line-clamp-4">
                {changeRequests[0].notes}
              </p>
              <p className="mt-2 font-mono text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                {new Date(changeRequests[0].created_at).toLocaleString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          )}

          {/* Approval Info */}
          {quotation.approved_at && (
            <div className={cn(
              'rounded-2xl shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)] backdrop-blur-sm p-5',
              STATUS_RAIL_BASE,
              'bg-violet-50/80 dark:bg-violet-900/20 before:bg-violet-500',
            )}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-2">
                Customer Approval
              </h3>
              {quotation.approval && (
                <div className="mb-1">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {quotation.approval.name}
                  </p>
                  {quotation.approval.phone && (
                    <p className="text-sm text-violet-700 dark:text-violet-300 tabular-nums">
                      {quotation.approval.phone}
                    </p>
                  )}
                </div>
              )}
              <p className="font-mono text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">
                {new Date(quotation.approved_at).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
              {quotation.approval ? (
                <div className="mt-2 space-y-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                  {quotation.approval.ip && <p className="tabular-nums">IP {quotation.approval.ip}</p>}
                  {quotation.approval.user_agent && (
                    <p className="truncate" title={quotation.approval.user_agent}>
                      {quotation.approval.user_agent}
                    </p>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  Approver not recorded (approved before this was added)
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Convert to Sales Order Modal — records the customer's PO */}
      <Modal
        open={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        title="Convert to Sales Order"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowConvertModal(false)} disabled={createSoMutation.isPending || uploading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => { void handleCreateSalesOrder(); }}
              disabled={createSoMutation.isPending || uploading}
            >
              {uploading ? 'Uploading PO…' : createSoMutation.isPending ? 'Creating…' : 'Create Sales Order'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Creates a Sales Order from quote{' '}
            <strong className="font-mono tabular-nums">{quotation.quote_number}</strong>{' '}
            ({formatCurrency(quotation.grand_total, currency)}) and records the customer's purchase order
            against it. Stock is deducted only when deliveries are recorded.
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Customer PO number <span className="text-danger-600">*</span>
            </label>
            <Input
              value={poNumber}
              onChange={(v) => { setPoNumber(v); if (poNumberError) setPoNumberError(null); }}
              placeholder="e.g. PO-7781"
              aria-label="Customer PO number"
            />
            {poNumberError && <p className="text-xs text-danger-600 mt-1">{poNumberError}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">PO date</label>
              <Input type="date" value={poDate} onChange={setPoDate} aria-label="Customer PO date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Expected delivery</label>
              <Input type="date" value={expectedDelivery} onChange={setExpectedDelivery} aria-label="Expected delivery date" />
            </div>
          </div>
          <TextArea
            label="Shipping address"
            rows={2}
            value={shippingAddress}
            onChange={setShippingAddress}
            placeholder="Defaults to the customer's billing address"
          />
          <TextArea
            label="Notes"
            rows={2}
            value={soNotes}
            onChange={setSoNotes}
            placeholder="Terms, delivery instructions… (printed on the pro-forma)"
          />
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Customer PO document <span className="font-normal text-neutral-500">(PDF / image, ≤10 MB, optional)</span>
            </label>
            <FileUpload
              variant="button"
              accept={DOCUMENT_ACCEPT}
              maxSize={DOCUMENT_MAX_BYTES}
              preview={poFile?.name ?? null}
              onUpload={(files: File[]) => setPoFile(files[0] ?? null)}
            />
            {poFile && (
              <p className="text-[11px] text-success-700 dark:text-success-300 mt-1">{poFile.name} selected — uploaded when you create the order.</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Send Modal */}
      <Modal
        open={showSendModal}
        onClose={() => {
          setShowSendModal(false);
          setGeneratedLink('');
        }}
        title="Send Quote"
        footer={
          generatedLink ? (
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => { setShowSendModal(false); setGeneratedLink(''); }}>
                Done
              </Button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={sendMutation.isPending}
              >
                {sendMutation.isPending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          )
        }
      >
        <div className="space-y-4">
          {generatedLink ? (
            <div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                Share this link with the customer:
              </p>
              <div className="flex items-center gap-2 bg-neutral-100/80 dark:bg-neutral-800/80 rounded-lg p-3">
                <p className="font-mono text-sm text-brand-600 dark:text-brand-400 truncate flex-1 tabular-nums">
                  {generatedLink}
                </p>
                <button
                  type="button"
                  onClick={copyLink}
                  className="p-2 rounded-lg hover:bg-neutral-200/80 dark:hover:bg-neutral-700/80 transition-colors"
                  title="Copy link"
                  aria-label="Copy approval link"
                >
                  <CopyIcon className="h-4 w-4 text-neutral-600 dark:text-neutral-400" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <RadioGroup
                label="Send Via"
                variant="horizontal"
                options={[
                  { value: 'LINK', label: 'Approval Link' },
                  { value: 'WHATSAPP', label: 'WhatsApp' },
                  { value: 'EMAIL', label: 'Email' },
                ]}
                value={sendChannel}
                onChange={(v) => {
                  setSendChannel(v as SendChannel);
                  // Phone and email recipients share one state slot — carrying a
                  // value across a channel switch would submit the wrong format.
                  setSendRecipient('');
                }}
              />
              {sendChannel === 'WHATSAPP' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Phone Number
                  </label>
                  <Input
                    variant="phone"
                    defaultDial={dial}
                    value={sendRecipient}
                    onChange={(v) => setSendRecipient(v)}
                    placeholder="98765 43210"
                  />
                </div>
              )}
              {sendChannel === 'EMAIL' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={sendRecipient}
                    onChange={(v) => setSendRecipient(v)}
                    placeholder="customer@example.com"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Cancel confirmation modal — destructive, requires explicit confirm. */}
      <Modal
        open={showCancelModal}
        onClose={closeCancelModal}
        title="Cancel this quotation?"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={closeCancelModal}
              disabled={cancelMutation.isPending || verifyingPin}
            >
              Keep quote
            </Button>
            <Button
              variant="danger"
              onClick={handleCancel}
              disabled={
                cancelMutation.isPending ||
                verifyingPin ||
                (!isManager && (!approverId || !/^\d{4,6}$/.test(approverPin)))
              }
            >
              {verifyingPin
                ? 'Checking PIN…'
                : cancelMutation.isPending
                  ? 'Cancelling…'
                  : 'Cancel quote'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-700 dark:text-neutral-200">
          {quotation.quote_number} will be moved to <strong>Cancelled</strong>.
          {isApproved
            ? ' The customer-facing approval link will stop working.'
            : ''}
          {' '}This cannot be undone.
        </p>
        {advancePaid > 0 && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-neutral-700 dark:text-neutral-200">
              The customer paid a down payment of{' '}
              <strong>{formatCurrency(advancePaid, currency)}</strong>. Choose how to refund it.
            </p>
            <RadioGroup
              label="Refund the down payment"
              options={[
                { value: 'ORIGINAL', label: 'To the original payment method' },
                { value: 'STORE_CREDIT', label: 'As store credit' },
              ]}
              value={refundMode}
              onChange={(v) => setRefundMode(v as QuotationRefundMode)}
            />
          </div>
        )}
        {!isManager && (
          <div className="mt-4 space-y-3 rounded-xl bg-warning-50 dark:bg-warning-900/20 p-4">
            <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
              Only a manager can cancel a quote. Ask a manager to pick their name and type their PIN.
            </p>
            {approversLoading ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading managers…</p>
            ) : approvers.length === 0 ? (
              <p className="text-sm text-neutral-700 dark:text-neutral-200">
                No manager for this store has a POS PIN yet. An owner can set one on the Users page.
              </p>
            ) : (
              <>
                <RadioGroup
                  label="Manager approving"
                  options={approvers.map((a) => ({
                    value: a.id,
                    label: `${a.name} (${a.role === 'OWNER' ? 'Owner' : 'Manager'})`,
                  }))}
                  value={approverId}
                  onChange={(v) => setApproverId(v)}
                />
                <Input
                  label="Manager PIN"
                  type="password"
                  inputMode="numeric"
                  value={approverPin}
                  onChange={(v) => setApproverPin(v.replace(/\D/g, '').slice(0, 6))}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Quotation Detail"
      breadcrumbs={[
        { label: 'Quotations', href: '/quotations' },
        { label: id?.slice(0, 8).toUpperCase() ?? '' },
      ]}
    >
      <ErrorBoundary>
        <QuotationDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
